import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { ToastService } from "src/app/shared/toast.service";
import { BuildingSurveyViewModel } from "../../../shared/Models/buildingTableViewModel";
import { AddBuildingRangeToNewBuildingGroupDto } from "../../../shared/Models/write/AddBuildingRangeToNewBuildingGroupDto";
import { BuildingGroupService } from "../../building-list/services/building-group.service";
import { Area } from "../../../shared/Models/read/Area";
import { SessionService } from "../../survey/shared/services/session.service";
import { TsaRemovalService } from "src/app/shared/tsa-removal.service";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";

@Component({
  selector: "app-building-group-add-building-to-new-group",
  templateUrl: "./building-group-add-building-to-new-group.component.html",
  styleUrls: ["./building-group-add-building-to-new-group.component.scss"],
})
export class BuildingGroupAddBuildingToNewGroupComponent
  implements OnInit, OnDestroy
{
  @Input() areaId: string;
  @Input() buildings: Array<BuildingSurveyViewModel>;
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @Output() reloadBuildingList: EventEmitter<any> = new EventEmitter();
  multipleInsideSurveysAvailble: boolean;
  buildingsWithInsideSurvey: Array<BuildingSurveyViewModel>;
  selectedBuildingId: string = null;
  currentArea: Area;
  buildingGroupName: String;

  constructor(
    public activeModal: NgbActiveModal,
    private buildingGroupService: BuildingGroupService,
    private toastService: ToastService,
    private sessionService: SessionService,
    private tsaRemovalService: TsaRemovalService,
    private dialogService: DialogService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  ngOnInit() {
    this.currentArea = this.sessionService.getBlock();
    const addBuildingRangeToNewBuildingGroupDto =
      new AddBuildingRangeToNewBuildingGroupDto({
        buildingIds: this.buildings.map((b) => b.buildingId),
        areaId: this.areaId,
        surveyId: "surveyId",
      });
    this.buildingGroupService
      .getTempBuildingGroupName(addBuildingRangeToNewBuildingGroupDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => (this.buildingGroupName = response.groupName));
  }

  openAddToExistingBuildingGroupModal() {
    this.activeModal.close("open-add-to-existing-modal");
  }

  returnToNewBuildingGroupOverview() {
    this.multipleInsideSurveysAvailble = false;
  }

  onSelectedBuilding(id) {
    this.selectedBuildingId = id;
  }

  onSave() {
    const error = this.getValidationError();
    if (error) return;

    const surveyId = this.getSelectedSurvey();
    if (surveyId === undefined) return;

    var detailText =
      this.tsaRemovalService.getRemarkForBuildingSurveyViewModels(
        this.buildings,
        ""
      );

    if (detailText)
      this.dialogService
        .openDialog({
          headerText: $localize`:@@buildinggroup.addbuilding.newbuildinggroup:Add building to group`,
          detailText: detailText,
          dialogType: DialogType.Continue,
          zIndex: 99999,
          fullscreen: true,
        })
        .pipe(first())
        .subscribe((feedback) => {
          if (feedback) this.handleCreateBuildingGroup(surveyId);
        });
    else this.handleCreateBuildingGroup(surveyId);
  }

  handleCreateBuildingGroup(surveyId: string) {
    const addBuildingRangeToNewBuildingGroupDto =
      new AddBuildingRangeToNewBuildingGroupDto({
        buildingIds: this.buildings.map((b) => b.buildingId),
        areaId: this.areaId,
        surveyId: surveyId,
      });

    this.buildingGroupService
      .addBuildingsToNewGroup(addBuildingRangeToNewBuildingGroupDto)
      .pipe(first())
      .subscribe(
        (res) => {
          this.passEntry.emit(
            this.buildings.forEach((b) => {
              this.reloadBuildingList.emit();
            })
          );
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@bg.buildinggroupcreate.error:Something went wrong while creating the new building group.`
          );
        }
      );
  }

  private getValidationError(): boolean {
    const emptyOrNotInUseBuildings = this.buildings.some((building) =>
      [BuildingTypeEnum.EmptyParcel, BuildingTypeEnum.NotInUse].includes(
        building.buildingType
      )
    );

    if (emptyOrNotInUseBuildings) {
      this.toastService.showWarnToast(
        $localize`:@@validation.new.buildinggroup.invalidtype:Invalid building type`,
        $localize`:@@validation.new.buildinggroup.invalidtype.notinuse:Buildings of type EmptyParcel or NotInUSe are not allowed in a building group`
      );
      return true;
    }

    const anyNoAccessBuildings = this.buildings.some(
      (building) => building.buildingType === BuildingTypeEnum.NoAccess
    );
    const allNoAccesBuildings = this.buildings.every(
      (building) => building.buildingType === BuildingTypeEnum.NoAccess
    );

    if (anyNoAccessBuildings && !allNoAccesBuildings) {
      this.toastService.showWarnToast(
        $localize`:@@validation.new.buildinggroup.invalidtype:Invalid building type`,
        $localize`:@@validation.new.buildinggroup.invalidtype.noaccess:Can only add NoAccess building if all buildings are of type NoAccess`
      );
      return true;
    }

    return false;
  }

  private getSelectedSurvey(): string {
    let surveyId;
    this.buildingsWithInsideSurvey = this.getBuildingsWithInsideSurveys();

    if (
      !this.buildingsWithInsideSurvey ||
      this.buildingsWithInsideSurvey.length === 0
    )
      surveyId = null;

    if (this.buildingsWithInsideSurvey.length === 1)
      surveyId = this.buildingsWithInsideSurvey[0].insideSurveyId;

    if (this.buildingsWithInsideSurvey.length > 1)
      surveyId = this.selectSurveyId();

    return surveyId;
  }

  private getBuildingsWithInsideSurveys(): BuildingSurveyViewModel[] {
    return this.buildings.filter((b) => b.insideSurveyId);
  }

  private selectSurveyId(): string {
    if (this.selectedBuildingId === null) {
      this.multipleInsideSurveysAvailble = true;
      return undefined;
    }
    return this.buildings.find((b) => b.buildingId == this.selectedBuildingId)
      .insideSurveyId;
  }

  private arrayMaxUnits(buildingSurveys: Array<BuildingSurveyViewModel>) {
    let max = -Infinity;
    for (let i = 0; i < buildingSurveys.length; i++)
      if (buildingSurveys[i].totalNumberOfUnits > max)
        max = buildingSurveys[i].totalNumberOfUnits;

    return max;
  }

  private comparers(a, b) {
    if (a > b) return +1;
    if (a < b) return -1;
    return 0;
  }
}
